<template>
    <div>
        <div v-if="error" class="alert alert-danger">
            <strong>Erreur de compilation :</strong>
            <pre>{{ error }}</pre>
        </div>
        <component 
            v-else-if="compiledComponent" 
            :is="compiledComponent" 
            :format="format"
            :data="componentData"
            :client="clientData"
        />
        <div v-else class="text-muted">
            <p>Aucun composant défini</p>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import store from '../../store';
import database from '../../services/database.js';
import workers from '../../services/workers.js';
import users from '../../services/users.js';
import treatments from '../../services/treatments.js';
import library from '../../services/library.js';
import projects from '../../services/projects.js';
import code from '../../services/code.js';
import document_service from '../../services/document.js';
import * as json2csv from 'json2csv';
import clone from 'clone';
import showdown from 'showdown';
import edit_tabulator from '../edit_tabulator.vue';
import ChartDisplay from '../ChartDisplay.vue';
import json_schema_editor from '../json_schema_editor.vue';
import ace_editor from '../ace_editor.vue';
import ScrapingCore from '../scraping_core.vue';
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import Chart from 'chart.js/auto';
import $ from 'jquery';
import '../../../public/js/query-builder.min.js';
import '../../../public/js/query-builder-fr.js';
import '../../../public/js/query-builder.default.min.css';

const componentFilesContext = require.context('../vueComponent', false, /\.vue$/);

function normalizeFileName(contextKey) {
    return contextKey.replace(/^\.\//, '');
}

const componentFileRegistry = componentFilesContext.keys().reduce((acc, key) => {
    const fileName = normalizeFileName(key);
    const mod = componentFilesContext(key);
    acc[fileName] = mod && mod.default ? mod.default : mod;
    return acc;
}, {});

// Extrait le contenu du <template>...</template> RACINE d'un composant component_code, en
// tenant compte des <template> imbriqués (ex: <template v-for="..."> / <template v-if="...">,
// utilisés pour grouper plusieurs éléments sans wrapper DOM superflu). Un simple regex non-gourmand
// (/<template>([\s\S]*?)<\/template>/) s'arrête à la PREMIÈRE balise </template> rencontrée, donc
// tronque silencieusement le template dès qu'un <template v-for>/<template v-if> imbriqué apparaît
// avant la fin réelle du composant - ce qui a provoqué un rendu partiel (vue tronquée juste après
// le premier <template> imbriqué) sans aucune erreur visible côté utilisateur.
function extractOuterTemplate(code) {
    const startIdx = code.indexOf('<template');
    if (startIdx === -1) return null;
    const openTagEndIdx = code.indexOf('>', startIdx);
    if (openTagEndIdx === -1) return null;

    const tagRe = /<template\b[^>]*>|<\/template>/g;
    tagRe.lastIndex = openTagEndIdx + 1;
    let depth = 1;
    let m;
    while ((m = tagRe.exec(code)) !== null) {
        if (m[0] === '</template>') {
            depth--;
            if (depth === 0) {
                return code.slice(openTagEndIdx + 1, m.index);
            }
        } else {
            depth++;
        }
    }
    return null;
}

export default {
    props: {
        format: {
            default: "small"
        },
        init_params: {
        },
        tooltip: {
            default: false
        },
        // jwt d'un utilisateur simul\u00e9 (\"tester en tant que\"), fourni par un parent
        // (ex: Dashboard.vue lors d'une pr\u00e9visualisation). Utilis\u00e9 pour les appels
        // this.database / this.code du composant personnalis\u00e9 charg\u00e9 ici.
        test_jwt: {
            default: null
        },
        // Objet utilisateur simul\u00e9 (uid/identifiant/nom/prenom/groups) correspondant \u00e0
        // test_jwt, fourni par le m\u00eame parent. Expos\u00e9 comme this.user/$user au composant
        // personnalis\u00e9 \u00e0 la place du vrai utilisateur connect\u00e9 (store.state.user) le temps
        // de la pr\u00e9visualisation.
        test_user: {
            default: null
        }
    },
    data() {
        return {
            compiledComponent: null,
            componentData: null,
            clientData: null,
            error: null
        };
    },
    methods: {
        getComponentFromFile(fileName) {
            if (!fileName) return null;
            return componentFileRegistry[fileName] || null;
        },
        extendComponentOptions(userOptions = {}) {
            const userCreated = userOptions.created;
            const test_jwt = this.test_jwt;
            // Utilisateur simulé ("tester en tant que") si fourni par le parent, sinon le vrai
            // utilisateur connecté. C'est CE user qui doit être utilisé par les composants
            // personnalisés pour toute décision d'affichage basée sur l'identité/les groupes
            // (store.state.user resterait celui de l'admin en train de prévisualiser).
            const user = this.test_user || store.state.user;

            // Wrappers légers autour de database/code qui injectent automatiquement le jwt
            // de l'utilisateur simulé ("tester en tant que") quand ce composant dashboard est
            // prévisualisé pour un autre utilisateur - sans jamais toucher store.state.jwt.
            const database_wrapped = {
                get: (action, table, params) => database.get(action, table, params, test_jwt),
                request: (request, user_params) => database.request(request, user_params, test_jwt),
            };
            const code_wrapped = {
                code: (c, data) => code.code(c, data, test_jwt),
                run_by_name: (name, data, version) => code.run_by_name(name, data, version, test_jwt),
            };
            const treatments_wrapped = {
                create: (project, format, tabulator_data_csv, name_file_tabulator, tags, type, form_data, params, treatment_params, step_uid, treatment_params_config, url_files) =>
                    treatments.create(project, format, tabulator_data_csv, name_file_tabulator, tags, type, form_data, params, treatment_params, step_uid, treatment_params_config, url_files, test_jwt),
                stop: (id) => treatments.stop(id, test_jwt),
                get: (id, key) => treatments.get(id, key, test_jwt),
            };
            const document_wrapped = {
                getFile: (id, name, dowload, response_type, share) => document_service.getFile(id, name, dowload, response_type, share, test_jwt),
                getInfoFile: (id, share) => document_service.getInfoFile(id, share, test_jwt),
            };

            return Vue.extend({
                ...userOptions,
                props: Array.isArray(userOptions.props)
                    ? [...new Set([...userOptions.props, 'format', 'data', 'client'])]
                    : { ...(userOptions.props || {}), format: {}, data: {}, client: {} },
                components: {
                    edit_tabulator,
                    ChartDisplay,
                    json_schema_editor,
                    ace_editor,
                    ScrapingCore,
                    VuePdfEmbed,
                    ...(userOptions.components || {})
                },
                provide() {
                    return {
                        $database: database_wrapped,
                        $treatments: treatments_wrapped,
                        $document: document_wrapped,
                        $json2csv: json2csv,
                        $jquery: $,
                        $workers: workers,
                        $library: library,
                        $projects: projects,
                        $Chart: Chart,
                        $clone: clone,
                        $code: code_wrapped,
                        $user: user,
                    };
                },
                created() {
                    this.database = database_wrapped;
                    this.workers = workers;
                    this.treatments = treatments_wrapped;
                    this.document = document_wrapped;
                    this.library = library;
                    this.projects = projects;
                    this.json2csv = json2csv;
                    this.clone = clone;
                    this.Chart = Chart;
                    this.$ = $;
                    this.code = code_wrapped;
                    this.user = user;

                    const converter = new showdown.Converter({
                        tables: true,
                        strikethrough: true,
                        tasklists: true,
                        simpleLineBreaks: true,
                        emoji: true,
                        literalMidWordUnderscores: true,
                        ghCodeBlocks: true
                    });
                    this.markdown = (text) => converter.makeHtml(text);

                    if (typeof userCreated === 'function') {
                        userCreated.call(this);
                    }
                }
            });
        },
        async load(data) {
            console.log("Loading vueComponent with data:", data);
            
            // Nettoyer les anciens styles
            if (this._styleElement && this._styleElement.parentNode) {
                this._styleElement.parentNode.removeChild(this._styleElement);
                this._styleElement = null;
            }
            
            this.componentData = data;
            this.error = null;

            // Charger les données client si pas encore disponibles
            if (!this.clientData) {
                try {
                    var response = await users.getSelf(true);
                    this.clientData = response.client.details || null;
                } catch (err) {
                    console.warn("vueComponent: impossible de charger les données client", err);
                }
            }

            if (data && data.component_file) {
                const fileComponent = this.getComponentFromFile(data.component_file);
                if (!fileComponent) {
                    throw new Error(`Composant introuvable dans vueComponent: ${data.component_file}`);
                }
                this.compiledComponent = this.extendComponentOptions(fileComponent);
                return;
            }

            if (!data || !data.component_code) {
                console.warn("No component code provided");
                return;
            }

            try {
                this.compiledComponent = this.compileComponent(data.component_code);
            } catch (err) {
                console.error("Error compiling component:", err);
                this.error = err.message || err.toString();
            }
        },
        compileComponent(componentCode) {
            try {
                // Extraire les sections du composant
                const templateContent = extractOuterTemplate(componentCode);
                const scriptMatch = componentCode.match(/<script>([\s\S]*?)<\/script>/);
                const styleMatch = componentCode.match(/<style(?:\s+scoped)?>([\s\S]*?)<\/style>/);

                if (templateContent === null) {
                    throw new Error("Template non trouvé. Utilisez <template>...</template>");
                }

                const template = templateContent.trim();
                let componentOptions = { template };

                // Parser et évaluer le script
                if (scriptMatch) {
                    const scriptContent = scriptMatch[1].trim();

                    // On ne peut pas se contenter de préfixer tout le script par "return " : si
                    // des instructions (const, function...) précèdent "export default {...}"
                    // (ex: gcr.vue définit "const GCR_SEARCH_SCRIPT = `...`;" avant son export),
                    // "return" suivi d'un commentaire/saut de ligne déclenche l'ASI ("return;"
                    // inséré automatiquement), et le "{ props: ..., data() {...}, ... }" restant
                    // est alors interprété comme un BLOC de code (pas un objet), ce qui casse sur
                    // "data() {" -> "Unexpected token '{'". On remplace donc "export default" par
                    // une affectation de variable, et on ajoute le "return" de cette variable
                    // seulement à la toute fin, après que toutes les instructions précédentes
                    // aient pu s'exécuter normalement.
                    let cleanScript = scriptContent.replace(/export\s+default\s*/, 'var __component_options__ = ');

                    // Évaluer le script pour obtenir l'objet d'options
                    try {
                        // Créer une fonction qui retourne l'objet d'options
                        const optionsFunction = new Function(cleanScript + '\nreturn __component_options__;');
                        const userOptions = optionsFunction();
                        
                        // Fusionner les options
                        componentOptions = {
                            ...componentOptions,
                            ...userOptions,
                            // S'assurer que props inclut toujours format, data et client
                            props: Array.isArray(userOptions.props) 
                                ? [...new Set([...userOptions.props, 'format', 'data', 'client'])]
                                : { ...userOptions.props, format: {}, data: {}, client: {} },
                            // Fusionner les components (ne pas écraser edit_tabulator/ChartDisplay)
                            components: {
                                ...componentOptions.components,
                                ...(userOptions.components || {})
                            }
                        };
                    } catch (err) {
                        console.error("Error parsing script:", err);
                        throw new Error("Erreur dans le script: " + err.message);
                    }
                }

                // Gérer les styles
                if (styleMatch) {
                    const style = styleMatch[0];
                    const isScoped = style.includes('scoped');
                    const styleContent = styleMatch[1].trim();
                    
                    console.log("Style trouvé:", styleContent);
                    console.log("Est scoped:", isScoped);
                    
                    // Créer un nouvel élément style
                    this._styleElement = document.createElement('style');
                    this._styleElement.type = 'text/css';
                    
                    if (isScoped) {
                        // Pour un style scoped, on ajoute un attribut data unique
                        const scopeId = 'data-v-' + this._uid;
                        console.log("Scope ID:", scopeId);
                        
                        // Utiliser un sélecteur descendant pour cibler les éléments à l'intérieur du scope
                        const scopedCss = styleContent.replace(
                            /([^\r\n,{}]+)(,(?=[^}]*{)|\s*{)/g,
                            (match, selector, separator) => {
                                // Nettoyer le sélecteur
                                const cleanSelector = selector.trim();
                                // Ajouter [scopeId] comme sélecteur descendant
                                return `[${scopeId}] ${cleanSelector}${separator}`;
                            }
                        );
                        console.log("CSS scopé:", scopedCss);
                        this._styleElement.textContent = scopedCss;
                        
                        // Ajouter l'attribut scope au composant racine
                        const originalMounted = componentOptions.mounted;
                        componentOptions.mounted = function() {
                            console.log("Mounted - ajout de l'attribut scope", scopeId);
                            console.log("$el:", this.$el);
                            if (this.$el && this.$el.setAttribute) {
                                this.$el.setAttribute(scopeId, '');
                                console.log("Attribut ajouté");
                            }
                            if (originalMounted) {
                                originalMounted.call(this);
                            }
                        };
                    } else {
                        console.log("CSS non-scopé:", styleContent);
                        this._styleElement.textContent = styleContent;
                    }
                    
                    document.head.appendChild(this._styleElement);
                    console.log("Style ajouté au head");
                }

                // Créer le composant Vue
                return this.extendComponentOptions(componentOptions);
            } catch (err) {
                console.error("Compilation error:", err);
                throw err;
            }
        }
    },
    beforeDestroy() {
        // Nettoyer les styles ajoutés
        if (this._styleElement && this._styleElement.parentNode) {
            this._styleElement.parentNode.removeChild(this._styleElement);
        }
    }
};
</script>

<style scoped>
.alert {
    padding: 10px;
    border-radius: 4px;
    margin: 10px 0;
}
.alert-danger {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
}
pre {
    background-color: #f4f4f4;
    padding: 10px;
    border-radius: 4px;
    overflow-x: auto;
}
</style>

<style>
/* Styles globaux pour le markdown rendu dans les composants dynamiques */
.component >>> em,
.component >>> i {
    font-style: italic !important;
}

.component >>> strong,
.component >>> b {
    font-weight: bold !important;
}

.component >>> table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
}

.component >>> table th,
.component >>> table td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
}

.component >>> table th {
    background-color: #f2f2f2;
    font-weight: bold;
}

.component >>> table tr:nth-child(even) {
    background-color: #f9f9f9;
}

.component >>> code {
    background-color: #f4f4f4;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
}

.component >>> pre code {
    display: block;
    padding: 10px;
    overflow-x: auto;
}

.component >>> blockquote {
    border-left: 4px solid #ddd;
    padding-left: 1em;
    margin-left: 0;
    color: #666;
}

.component >>> ul,
.component >>> ol {
    padding-left: 2em;
}

.component >>> h1, .component >>> h2, 
.component >>> h3, .component >>> h4, 
.component >>> h5, .component >>> h6 {
    margin-top: 1em;
    margin-bottom: 0.5em;
    font-weight: bold;
}
</style>
